import { BuildNode, NodeSizes } from "@common/build/types";
import * as css from "css";
import { CSSProperties } from "react";
import { setValueAtPath } from "./jsxHelpers";






export const _parseStyleString = (styleString: string | null): CSSProperties => {
    if (!styleString?.trim()) return {};

    // Wrap the style string so that the css parser can understand it as a rule block
    const parsed = css.parse(`.temp { ${styleString} }`);

    const firstRule = parsed.stylesheet?.rules?.[0];
    const declarations: any[] = (firstRule && (firstRule as any).declarations) || [];

    const result: Record<string, string> = {};

    for (const decl of declarations) {
        if (decl.type !== "declaration") continue;
        const property = (decl.property as string)?.trim();
        const value = (decl.value as string)?.trim();
        if (!property || !value) continue;
        result[(property)] = value;
    }

    return result as CSSProperties;
};

const _convertRemEmToPx = (value: string): string => {
    const remMatch = value.match(/^([\d.]+)rem$/i);
    if (remMatch) {
        return `${parseFloat(remMatch[1]) * 16}px`;
    }
    const emMatch = value.match(/^([\d.]+)em$/i);
    if (emMatch) {
        return `${parseFloat(emMatch[1]) * 16}px`;
    }
    return value;
};

const _consolidateProperties = (sourceProperties: string[], targetProperty: string, target: Record<string, any>) => {
    const collected: string[] = [];
    let hasAny = false;
    sourceProperties.forEach(prop => {
        const value = target[prop as keyof CSSProperties];
        collected.push(value ?? "0");
        if (value !== undefined) hasAny = true;
    });
    if (hasAny) {
        (target as Record<string, string>)[targetProperty] = collected.join(" ");
        sourceProperties.forEach(prop => delete target[prop as keyof CSSProperties]);
    }
};


export const applyInlineStylesOnNode = (
    styleInput: string | CSSProperties | HTMLElement | null | undefined,
    node: BuildNode,
    path: (string | number)[] = ["attr", "style"],
): BuildNode => {
    // Convert the inline style value into a plain record for easier mutation
    let baseStyles: Record<string, any> = {};
    if (typeof styleInput === "string") {
        baseStyles = _parseStyleString(styleInput);
    } else if (styleInput && (styleInput as HTMLElement).getAttribute) {
        // HTMLElement provided
        const elem = styleInput as HTMLElement;
        baseStyles = _parseStyleString(elem.getAttribute("style"));
    } else if (styleInput && typeof styleInput === "object") {
        baseStyles = { ...(styleInput as Record<string, string>) };
    }

    const newStyles: Record<string, any> = { ...baseStyles };

    if (newStyles.display === "flex" && !newStyles.flexDirection) {
        newStyles.flexDirection = "row";
    }

    if (!node.nodeSize) node.nodeSize = { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM };

    const widthProps = ["width", "minWidth"];
    const heightProps = ["height", "minHeight"];

    [...widthProps, ...heightProps].forEach(prop => {
        const val = newStyles[prop as keyof typeof newStyles] as string | undefined;
        if (!val) return;

        newStyles[prop as keyof typeof newStyles] = val;

        if (val === "100%" || val === "100vh" || val === "100vw") {
            node.nodeSize![widthProps.includes(prop) ? "width" : "height"] = NodeSizes.FILL;
        } else if (val === "fit-content" || val === "auto") {
            node.nodeSize![widthProps.includes(prop) ? "width" : "height"] = NodeSizes.FIT;
            delete newStyles[prop as keyof typeof newStyles];
        }
        
        else {
            node.nodeSize![widthProps.includes(prop) ? "width" : "height"] = NodeSizes.CUSTOM;
        }
        if (prop === "height" && (val === "0" || val === "0px") && (newStyles.flexGrow === 1 || newStyles.flexGrow === "1")) {
            node.nodeSize!.height = NodeSizes.FILL;
            // delete newStyles[prop as keyof typeof newStyles];
        }
    });

    if (newStyles.background) {
        if (newStyles.background.startsWith("url(")) {
            newStyles.backgroundImage = newStyles.background as string;
        } else if (/^#(?:[0-9a-fA-F]{3}){1,2}$/.test(newStyles.background as string)) {
            newStyles.backgroundColor = newStyles.background as string;
        }
    }

    _consolidateProperties(["paddingTop", "paddingRight", "paddingBottom", "paddingLeft"], "padding", newStyles);
    _consolidateProperties(["marginTop", "marginRight", "marginBottom", "marginLeft"], "margin", newStyles);
    _consolidateProperties(
        ["borderTopLeftRadius", "borderTopRightRadius", "borderBottomRightRadius", "borderBottomLeftRadius"],
        "borderRadius",
        newStyles,
    );

    for (const key in newStyles) {
        if (typeof newStyles[key] === "string") {
            newStyles[key] = _convertRemEmToPx(newStyles[key]);
        }
    }

    // lazy fix, but consider scrollable elements later
    if ((node.name === "div" || node.name === "repeaterDiv") && newStyles.display === "flex" && !newStyles.justifyContent && !newStyles.alignItems) {
        newStyles.justifyContent = "center";
        newStyles.alignItems = "center";
    }

    setValueAtPath(node, path, newStyles as CSSProperties);

    return node;
};


